import { useEffect } from 'react';
import {
  useQuery, useQueryClient, UseQueryResult, useInfiniteQuery, UseInfiniteQueryResult, InfiniteData,
} from '@tanstack/react-query';
import { getDevicesConnectivity } from '../../services/DeviceService';
import { DeviceConnectivityResponse } from '../../types/Devices';
import { FeaturesManagement } from '../../../featuresControl';
import { mergeNewestIntoFirstPage } from '../../../utils/queryFilters';
import { stitchConnectivityPages } from './stitchConnectivityPages';

function useGetDeviceConnectivity(fleetId: string, query = ''): UseQueryResult<DeviceConnectivityResponse> {
  return useQuery(
    ['device-connectivity', fleetId],
    async (): Promise<DeviceConnectivityResponse> => getDevicesConnectivity(fleetId),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      select: (data) => data,
      refetchInterval: FeaturesManagement.productivity.refetchInterval,
    },
  );
}

export function useGetDeviceConnectivityLazy(fleetId: string, payload?: any, query?: string, deps?: any): UseInfiniteQueryResult<DeviceConnectivityResponse> {
  const queryClient = useQueryClient();
  const queryKey = ['devices-connectivity-origin-lazy', fleetId, deps];

  const infiniteQuery = useInfiniteQuery<any>(
    queryKey,
    async ({ pageParam = query }): Promise<DeviceConnectivityResponse> => getDevicesConnectivity(fleetId, pageParam),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      select: (data) => ({
        ...data,
        pages: stitchConnectivityPages(data.pages),
      }),
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    },
  );

  const { data: latestPage } = useQuery(
    ['devices-connectivity-origin-lazy-latest', fleetId, deps, query],
    async (): Promise<DeviceConnectivityResponse> => getDevicesConnectivity(fleetId, query),
    {
      retry: 1,
      retryDelay: 3000,
      refetchOnWindowFocus: false,
      refetchInterval: FeaturesManagement.productivity.refetchIntervalLazyLists,
      enabled: !!infiniteQuery.data,
    },
  );

  useEffect(() => {
    if (!latestPage) return;
    queryClient.setQueryData<InfiniteData<DeviceConnectivityResponse>>(
      queryKey,
      (existing) => mergeNewestIntoFirstPage(existing, latestPage),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestPage]);

  return infiniteQuery;
}

export default useGetDeviceConnectivity;
